import React from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import Icon from 'components/Icon';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import type { components } from 'apiTyped/types';
import type { DistributionGlobalDataFilter } from 'apiTyped/hooks/global_data_view.query';

export type BrandWineRow = components['schemas']['GDVDataWinesDetailSchema'];
export type BrandWineScope = Omit<
  DistributionGlobalDataFilter,
  'aggregation_type'
>;

export const BrandExpandButton: React.FC<{
  name: string;
  expanded: boolean;
  onClick: () => void;
}> = ({ name, expanded, onClick }) => {
  const label = `${I18n.t(
    expanded
      ? 'v2GlobalView.brandRows.collapse'
      : 'v2GlobalView.brandRows.expand'
  )} ${name}`;
  return (
    <ExpandButton
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      $expanded={expanded}
      onClick={onClick}
    >
      <Icon icon="chevronRight" alt="" aria-hidden="true" />
    </ExpandButton>
  );
};

export const BrandWineRows: React.FC<{
  brandId: number;
  filter: BrandWineScope;
  colSpan: number;
  renderRow: (_wine: BrandWineRow, _index: number) => React.ReactNode;
}> = ({ brandId, filter, colSpan, renderRow }) => {
  const { rangePrices, ...scope } = filter;
  const { data, isLoading, isError } = useTypedQuery(
    'get',
    '/global_data_view/wines',
    {
      params: {
        query: {
          ...scope,
          aggregation_type: 'wine',
          marque_id: scope.use_custom_brands ? null : [brandId],
          ...(scope.use_custom_brands
            ? {
                client_and_rivals_only: false,
                selected_client_ref_ids: null,
                selected_client_custom_ids: [brandId]
              }
            : {}),
          min_price: rangePrices?.[0] ?? null,
          max_price: rangePrices?.[1] ?? null
        }
      },
      queryOpts: { placeholderData: undefined }
    }
  );
  const rows = data?.data ?? [];
  if (isLoading || isError || !rows.length) {
    const message = isLoading ? 'loading' : isError ? 'error' : 'empty';
    return (
      <tr>
        <StatusCell colSpan={colSpan} role={isError ? 'alert' : undefined}>
          {I18n.t(`v2GlobalView.brandRows.${message}`)}
        </StatusCell>
      </tr>
    );
  }
  return <>{rows.map(renderRow)}</>;
};

const ExpandButton = styled.button<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  padding: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  vertical-align: middle;
  .icon {
    width: 14px;
    height: 14px;
    transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
  }
  svg {
    width: 100%;
    height: 100%;
  }
`;

const StatusCell = styled.td`
  padding: 12px 48px;
  border-top: 1px solid var(--line-soft);
  color: var(--ink-muted);
`;
